/**
 * Entity class represents a game object in the ECS architecture.
 * Entities are essentially just IDs that components can be attached to.
 */
export class Entity {
  private static nextId: number = 0;
  private id: number;
  private name: string;
  private active: boolean;
  private tags: Set<string>;
  private graphics: any; // This will be properly typed once we set up the rendering system

  constructor(name: string = '') {
    this.id = Entity.nextId++;
    this.name = name;
    this.active = true;
    this.tags = new Set<string>();
    this.graphics = null;
  }

  /**
   * Get the unique ID of this entity
   */
  public getId(): number {
    return this.id;
  }

  /**
   * Add a tag to this entity
   */
  public addTag(tag: string): void {
    this.tags.add(tag);
  }

  /**
   * Remove a tag from this entity
   */
  public removeTag(tag: string): void {
    this.tags.delete(tag);
  }

  /**
   * Check if entity has a specific tag
   */
  public hasTag(tag: string): boolean {
    return this.tags.has(tag);
  }

  /**
   * Get all tags for this entity
   */
  public getTags(): string[] {
    return Array.from(this.tags);
  }

  /**
   * Get the name of this entity
   */
  public getName(): string {
    return this.name;
  }

  /**
   * Set the name of this entity
   */
  public setName(name: string): void {
    this.name = name;
  }

  /**
   * Check if entity is active
   */
  public isActive(): boolean {
    return this.active;
  }

  /**
   * Set entity active state
   */
  public setActive(active: boolean): void {
    this.active = active;
  }

  /**
   * Get the graphics object for this entity
   */
  public getGraphics(): any {
    return this.graphics;
  }

  /**
   * Set the graphics object for this entity
   */
  public setGraphics(graphics: any): void {
    this.graphics = graphics;
  }

  /**
   * Reset the entity ID counter (useful for testing)
   */
  public static resetIdCounter(): void {
    Entity.nextId = 0;
  }
}
